// react-client/src/features/feedback/components/FeedbackSearchTable.jsx
import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Chip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { useFeedbackStatus } from '../hooks/useFeedbackManagement';
import VoteButton from './VoteButton';

export default function FeedbackSearchTable({ feedbackItems, loading, onRowClick, onVoteChange, canModerate }) {
  const { t } = useTranslation('feedback');
  const {
    getVisibilityLabel,
    getVisibilityColor,
    getImplementationLabel,
    getImplementationColor,
  } = useFeedbackStatus();

  const columns = [
    {
      field: 'title',
      headerName: t('feedback:search.table.title'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'vote_action',
      headerName: t('feedback:search.table.votes'),
      width: 140,
      sortable: true,
      valueGetter: (value, row) => row.total_votes || 0,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <VoteButton
            feedbackId={params.row.feedback_id}
            userHasVoted={params.row.user_has_voted}
            totalVotes={params.row.total_votes || 0}
            onVoteChange={(data) => onVoteChange && onVoteChange(params.row.feedback_id, data)}
            variant="icon"
            size="small"
          />
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography 
              variant="body2" 
              fontWeight={params.row.user_has_voted ? 'bold' : 'normal'}
              color={params.row.user_has_voted ? 'primary' : 'text.secondary'}
            >
              {params.row.total_votes || 0}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'staff_member_name',
      headerName: t('feedback:search.table.submitted_by'),
      width: 150,
    },
    ...(canModerate ? [{
      field: 'department_name',
      headerName: t('feedback:search.table.department'),
      width: 150,
    }] : []),
    {
      field: 'status',
      headerName: t('feedback:search.table.status'),
      width: 180,
      renderCell: (params) => (
        <Chip
          label={getImplementationLabel(params.value)}
          color={getImplementationColor(params.value)}
          size="small"
          variant="filled"
        />
      ),
    },
    ...(canModerate ? [{
      field: 'visibility',
      headerName: t('feedback:search.table.visibility'),
      width: 130,
      renderCell: (params) => (
        <Chip
          label={getVisibilityLabel(params.value)}
          color={getVisibilityColor(params.value)}
          size="small"
          variant="filled"
        />
      ),
    }] : []),
    {
      field: 'created_at',
      headerName: t('feedback:search.table.created'),
      width: 150,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => (
        params.value ? format(params.value, 'MMM d, yyyy') : '-'
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={feedbackItems}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.feedback_id}
        onRowClick={(params) => onRowClick(params.row)}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
          sorting: {
            sortModel: [{ field: 'vote_action', sort: 'desc' }],
          },
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            minHeight: 'unset !important',
            maxHeight: 'none !important',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
          '& .MuiDataGrid-cell': {
            minHeight: 'unset !important',
            maxHeight: 'none !important',
            height: 'auto !important',
            display: 'flex !important',
            alignItems: 'center !important',
            lineHeight: '1.5 !important',
            padding: '12px 16px !important',
          },
        }}
      />
    </Box>
  );
}